import { ArrowDown, ArrowUp, ArrowUpDown, Trash } from 'lucide-react';
import * as React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { SelectionAction } from '../../containers_v2/client-companies/style/Style';
import { FlexDiv } from '../../containers_v2/products/style';
import { DandDDots } from '../../containers_v2/settings/subSettings/statusesAndTagsSettings';
import AddIcon from '../../styles/custom-icons/add';
import { DEFAULT_BUTTON_HEIGHT } from '../../styles/global/css/GlobalButton';
import { PrimaryColor, DarkGreySidely2, RedSidely, SidelyBlack } from '../../styles/global/css/Utils';
import { Translate, translateToString } from '../../styles/global/translate';
import { useFunctionState } from '../../utils/customHooks';
import Dropdown from '../dropdown/Dropdown';
import Dropdown2 from '../dropdown/Dropdown2';
import { DropdownData } from '../dropdown/model/Model';
import { ToolBarButton } from '../toolbarFilter/ToolbarButton';


export type TableSortColumn<Id, AdditionalValues = unknown> = {
	name: string,
	id: Id,
	additionalDropdownData?: DropdownData<AdditionalValues>[]
};

export type OrderBy<T extends TableSortColumn<unknown>> = {
	order_by: T['id'],
	descending: boolean,
	value: T,
	additionalValue?: NonNullable<T['additionalDropdownData']>[number]['value'],
}

export type OrderByNoValue<T extends TableSortColumn<unknown>> = Omit<OrderBy<T>, 'value'>;

export function removeValueFromOrderBy<T extends TableSortColumn<unknown>>(orderBy: OrderBy<T>): OrderByNoValue<T> {
	const { value: _, ...rest } = orderBy;
	return rest;
}

function getDefaultOrderBy<T extends TableSortColumn<unknown>>(columns: T[], defaultOrderBy?: OrderByNoValue<T>[], compareFn?: (a: T['id'], b: T['id']) => boolean): OrderBy<T>[] {
	if (!defaultOrderBy?.length) return [];
	return defaultOrderBy.map(value => ({ ...value, value: columns.find(column => compareFn?.(value.order_by, column.id) ?? column.id === value.order_by)! })).filter(value => value.value !== undefined);
}

export default function TableSort<T extends TableSortColumn<unknown>>(props: {
	defaultOrderBy?: OrderByNoValue<T>[],
	columns: T[],
	onSort: (sort: OrderBy<T>[]) => void,
	compareFn?: (a: T['id'], b: T['id']) => boolean,
}) {
	const [orderBy, setOrderBy] = useFunctionState<OrderBy<T>[]>(getDefaultOrderBy(props.columns, props.defaultOrderBy, props.compareFn), undefined, props.onSort);
	const isOpenState = React.useState<boolean>(false);
	const [_, setPopupOpen] = isOpenState;

	const dataList = React.useMemo(() => props.columns.map(value => ({ label: value.name, value })), [props.columns]);
	const ascendingList = React.useMemo(() => ['ascending', 'descending'].map(label => ({ label: translateToString(label), value: label === 'descending' })), []);

	React.useEffect(() => {
		if (props.defaultOrderBy && (props.columns?.length ?? 0) > 0) {
			setPopupOpen(true);
			setOrderBy(getDefaultOrderBy(props.columns, props.defaultOrderBy, props.compareFn));
		}
	}, [JSON.stringify(props.defaultOrderBy), JSON.stringify(props.columns)]);

	if (orderBy.length === 0) {
		return <Dropdown
			name={'sort-dd'}
			datalist={dataList}
			onChange={(value) => setOrderBy([{ order_by: value.value.id, descending: false, value: value.value, additionalValue: value.value.additionalDropdownData?.[0]?.value }])}
			JSXButton={() => <ToolBarButton><ArrowUpDown color={PrimaryColor} />{translateToString('sort')}</ToolBarButton>}
			dropdownStyle={{ optionHeight: '250px', optionWidth: 'fit-content', containerHeight: `${DEFAULT_BUTTON_HEIGHT}px` }}
		/>;
	}

	function onDragEnd({ source, destination }) {
		if (!destination || !source || source.index === destination.index) return;
		const result = [...orderBy];
		const [removed] = result.splice(source.index, 1);
		result.splice(destination.index, 0, removed);
		setOrderBy(result);
	}

	return <Dropdown2
		noClamp
		isOpenState={isOpenState}
		button={({ setIsOpen }) => <ToolBarButton onClick={() => setIsOpen(t => !t)} active onDelete={() => setOrderBy([])}>
			{orderBy.length === 1
				? orderBy[0].descending
					? <><ArrowUp size={16} color={PrimaryColor} />{orderBy[0].value.name}</>
					: <><ArrowDown size={16} color={PrimaryColor} />{orderBy[0].value.name}</>
				: <><ArrowUpDown size={20} color={PrimaryColor} /><Translate id='NUMBER_sorts' options={{ variables: [['NUMBER', orderBy.length.toString()]] }} /></>
			}
		</ToolBarButton>}
	>
		<>
			<DragDropContext onDragEnd={onDragEnd}>
				<Droppable droppableId='table-sort'>
					{(provided): React.ReactElement<HTMLElement> => (
						<FlexDiv {...provided.droppableProps} innerRef={provided.innerRef} className="table-sort" flow='column' align='stretch' gap='10px' scrollGutter padding='10px 4px 10px 0px'>
							{orderBy.map((sort, index) => {
								return <Draggable key={sort.value.name} draggableId={sort.value.name} index={index}>
									{(provided): React.ReactElement<HTMLElement> => (
										<OrderByElement key={sort.value.name} innerRef={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
											<DandDDots />
											<Dropdown
												name={'inner-sort-dd'}
												datalist={dataList}
												selectedValue={{ label: sort.value.name, value: sort.value }}
												onChange={(value) => {
													const newOrderBy = [...orderBy];
													newOrderBy[index].order_by = value.value.id;
													newOrderBy[index].value = value.value;
													newOrderBy[index].additionalValue = value.value.additionalDropdownData?.[0]?.value;
													setOrderBy(newOrderBy);
												}}
												dropdownStyle={{ optionHeight: '250px', width: '150px', optionWidth: 'fit-content' }} />
											{sort.value.additionalDropdownData !== undefined &&
												<Dropdown
													name='additional-values-dd'
													datalist={sort.value.additionalDropdownData}
													selectedValue={sort.value.additionalDropdownData.find(v => v.value === sort.additionalValue)}
													onChange={(value) => {
														const newOrderBy = [...orderBy];
														newOrderBy[index].additionalValue = value.value;
														setOrderBy(newOrderBy);
													}}
													searchable={false}
													dropdownStyle={{ optionHeight: '250px', width: '150px', optionWidth: '150px' }} />}
											<Dropdown
												name='ascending-dd'
												datalist={ascendingList}
												selectedValue={ascendingList.find(v => v.value === sort.descending)}
												onChange={(value) => {
													const newOrderBy = [...orderBy];
													newOrderBy[index].descending = value.value;
													setOrderBy(newOrderBy);
												}}
												searchable={false}
												dropdownStyle={{ optionHeight: '250px', width: '150px', optionWidth: '150px' }} />
											<SelectionAction hoverSvgColor={RedSidely}>
												<Trash size={16} color={DarkGreySidely2} onClick={() => setOrderBy(ob => ob.filter((_, i) => i !== index))} />
											</SelectionAction>
										</OrderByElement>
									)}
								</Draggable>;
							}
							)}
							{provided.placeholder}
						</FlexDiv>
					)}
				</Droppable>
			</DragDropContext>
			<Dropdown
				name={'inner-sort-dd'}
				datalist={dataList}
				onChange={(value) => setOrderBy(ob => [...ob, { order_by: value.value.id, descending: false, value: value.value, additionalValue: value.value.additionalDropdownData?.[0]?.value }])}
				JSXButton={() => <SortSetting hoverColor={PrimaryColor}>
					<AddIcon size={16} />
					<Translate id='add_sort' />
				</SortSetting>}
				dropdownStyle={{ optionHeight: '250px', width: '100%', containerWidth: '100%' }}
			/>
			<SortSetting hoverColor={RedSidely} onClick={() => setOrderBy([])} >
				<Trash size={16} />
				<Translate id='delete_sorts' />
			</SortSetting>
		</>
	</Dropdown2>;
}

export const SortSetting = styled.div<{
	hoverColor?: string,
	nowrap?: boolean,
	fitContent?: boolean,
	justifyContent?: React.CSSProperties['justifyContent'],
	noHoverable?: boolean,
	selected?: boolean,
}>`
	width: ${p => p.fitContent ? 'fit-content' : '100%'};
	border-radius: 5px;
	padding: 5px;
  	display: flex;
   	align-items: center;
    gap: 5px;
    font-size: 12px;
    white-space: ${p => p.nowrap ? 'nowrap' : 'normal'};
    color: ${DarkGreySidely2};
    & > svg {
		color: ${DarkGreySidely2};
	}
	${p => p.noHoverable ? '' : `
		cursor: pointer;
		&:hover {
			color: ${p.hoverColor ?? DarkGreySidely2};
			background-color: color-mix(in oklab, ${SidelyBlack}  3%, transparent);
			& > svg {
				color: ${p.hoverColor ?? DarkGreySidely2};
			}
		}`}
	justify-content: ${p => p.justifyContent ?? 'flex-start'};
	${p => p.selected ? 'background-color: color-mix(in oklab, black 5%, transparent);' : ''}
`;

const OrderByElement = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	height: 30px;
`;
